/**
 * Translation key helpers for custom-discipline user-facing labels.
 *
 * Field: `customDiscipline.group.{CODE}[.athlete].{field.key}`
 * Option: `customDiscipline.group.{CODE}[.athlete].{field.key}.{option.label}`
 * Action: `customDiscipline.group.{CODE}.actions.{action.key}`
 * Action tooltip: `customDiscipline.group.{CODE}.actions.{action.key}.tooltip`
 * Modal: `customDiscipline.group.{CODE}.modals.{modal.key}`
 * Icon column: `customDiscipline.group.{CODE}.iconColumns.{columnName}`
 * Group type name: `customDiscipline.group.{CODE}.meta.name`
 * Discipline name: `customDiscipline.discipline.{CODE}.name`
 */

import type { CustomDisciplineFieldScope } from "./enums.js";

export type CustomDisciplineLabelField = {
  scope: CustomDisciplineFieldScope | string;
  key: string;
};

export type CustomDisciplineLabelOption = {
  label: string;
};

/**
 * Builds the translation key for a custom-discipline field label.
 */
export const getCustomDisciplineFieldTranslationKey = (
  groupTypeCode: string,
  field: CustomDisciplineLabelField,
) => {
  const athleteSegment = field.scope === "ATHLETE" ? ".athlete" : "";
  return `customDiscipline.group.${groupTypeCode}${athleteSegment}.${field.key}`;
};

/**
 * Builds the translation key for a SELECT option label.
 */
export const getCustomDisciplineOptionTranslationKey = (
  groupTypeCode: string,
  field: CustomDisciplineLabelField,
  option: CustomDisciplineLabelOption,
) =>
  `${getCustomDisciplineFieldTranslationKey(groupTypeCode, field)}.${option.label}`;

/**
 * Builds the translation key for a custom-discipline group action label.
 */
export const getCustomDisciplineActionTranslationKey = (
  groupTypeCode: string,
  action: { key: string },
) => `customDiscipline.group.${groupTypeCode}.actions.${action.key}`;

/**
 * Builds the translation key for a GRID_ICON static tooltip.
 */
export const getCustomDisciplineActionTooltipTranslationKey = (
  groupTypeCode: string,
  action: { key: string },
) => `${getCustomDisciplineActionTranslationKey(groupTypeCode, action)}.tooltip`;

/**
 * Builds the translation key for a custom-discipline modal label.
 */
export const getCustomDisciplineModalTranslationKey = (
  groupTypeCode: string,
  modal: { key: string },
) => `customDiscipline.group.${groupTypeCode}.modals.${modal.key}`;

/**
 * Builds the translation key for a GRID_ICON column header.
 */
export const getCustomDisciplineIconColumnTranslationKey = (
  groupTypeCode: string,
  columnName: string,
) => `customDiscipline.group.${groupTypeCode}.iconColumns.${columnName}`;

/**
 * Builds the translation key for a group type display name.
 *
 * Uses `.meta.name` so it does not collide with a GROUP field whose key is `name`.
 */
export const getCustomDisciplineGroupTypeNameTranslationKey = (
  groupTypeCode: string,
) => `customDiscipline.group.${groupTypeCode}.meta.name`;

/**
 * Builds the translation key for a discipline display name.
 */
export const getCustomDisciplineNameTranslationKey = (disciplineCode: string) =>
  `customDiscipline.discipline.${disciplineCode}.name`;
